/**
 * 推廣活動 (campaigns; plan module `pointsPromotions`, native brands only): for a period, sales and
 * sign-ups that meet a rule's condition get the rule's gifts. Contract, request body and the rules
 * engine, shared by the API (which saves the gifts: apps/api/src/lib/campaigns.ts), the brand
 * portal and the member site.
 *
 * Rules:
 * - A campaign runs while active, from startAt to endAt (Hong Kong time, to the minute, both
 *   included), on its weekdays and, when set, from timeFrom to timeTo (Hong Kong time, both included).
 * - A sale counts for a running campaign when it also matches the campaign's levels (the member's
 *   level before the sale), shops and minimum amount. A sign-up counts when the new member's level
 *   matches; shops and the minimum amount do not apply to sign-ups.
 * - Each rule is one condition and its gifts:
 *   · 單次消費 (transaction, single): the sale's net amount is from min to max (either may be empty).
 *   · 累積消費 (transaction, accumulative): the member's net spend on sales that count for this
 *     campaign, this sale included, reaches min. Given once per member.
 *   · 購買商品 (product): the sale has at least `quantity` units of the SKUs (any of them; letter case
 *     and spaces ignored). Given once per sale, or with `repeat` once for every `quantity` units.
 *   · 新會員登記 (registration): a member signs up while the campaign runs.
 * - Gifts: extra points; a points multiplier (normal points × N, sales only); coupons from the coupon
 *   pool (a quantity of each). Every rule whose condition is met gives its gifts, so rules add up.
 * - Multipliers never add up: of every campaign and rule a sale meets, only the highest multiplier
 *   counts. Extra points and coupons of different rules and campaigns add up.
 * - Limits (0 = no limit): a rule's `limit` is how many times its gifts can be given in total; a
 *   campaign's totalLimit / memberLimit is how many sales or sign-ups can get something from it, in
 *   total / per member. A rule or campaign at its limit gives nothing more; the others carry on.
 * - Campaigns saved before rules existed (積分加倍推廣) read as one rule "any sale → their multiplier
 *   and extra points", so such a sale still earns floor(normal points × multiplier) + extra points.
 */
import { z } from 'zod';
import { AI_COPY_TONES, type AiCopyTone } from './ai-content.ts';
import type { SaleLine } from './contract.ts';
import { type LocalizedText, pickText } from './i18n.ts';
import { localizedTextSchema } from './schemas.ts';

// ---------------------------------------------------------------- contract

export type CampaignCondition =
  | { type: 'transaction'; mode: 'single' | 'accumulative'; min: number | null; max: number | null }
  | { type: 'product'; skus: string[]; quantity: number; repeat: boolean }
  | { type: 'registration' };
export type CampaignConditionType = CampaignCondition['type'];

export type CampaignGift = { type: 'points'; points: number } | { type: 'multiplier'; multiplier: number } | { type: 'coupon'; rewardId: string; quantity: number };

export interface CampaignRule {
  /** Stays the same while the campaign is edited (limits and the history refer to it). */
  id: string;
  condition: CampaignCondition;
  gifts: CampaignGift[];
  /** Times this rule's gifts can be given in total; 0 = no limit. */
  limit: number;
}

/** What the brand sets (campaignSchema). */
export interface CampaignSettings {
  name: LocalizedText;
  description: LocalizedText;
  /** 條款及細則 */
  terms: LocalizedText;
  imageKey: string | null;
  /** Hong Kong time "YYYY-MM-DDTHH:MM", both included. */
  startAt: string;
  endAt: string;
  /** 0 = Sunday … 6 = Saturday; null = every day. */
  weekdays: number[] | null;
  /** "HH:MM" Hong Kong time; both null = all day. */
  timeFrom: string | null;
  timeTo: string | null;
  /** null = every level / every shop. */
  tierIds: number[] | null;
  shopIds: string[] | null;
  minAmount: number | null;
  /** Sales / sign-ups that can get something, in total and per member; 0 = no limit. */
  totalLimit: number;
  memberLimit: number;
  rules: CampaignRule[];
  active: boolean;
  /** The tone picked for 「AI 生成內容」, so 「AI 生成圖片」 follows it; null = none picked. */
  aiTone?: AiCopyTone | null;
}

export interface CampaignAdmin extends CampaignSettings {
  id: string;
  /** Running number of the brand's campaigns, shown as the promotion id (campaignCode). */
  no: number;
  imageUrl: string | null;
  /** Sales / sign-ups that got something. */
  timesUsed: number;
  /** Members who got something. */
  members: number;
  /** Points given above normal points. */
  pointsGiven: number;
  couponsGiven: number;
  createdAt: string;
  updatedAt: string;
}

export type CampaignGiftPublic = { type: 'points'; points: number } | { type: 'multiplier'; multiplier: number } | { type: 'coupon'; title: LocalizedText; quantity: number };

/** Shown on the member site while running or starting within 30 days. */
export interface CampaignPublic {
  id: string;
  name: LocalizedText;
  description: LocalizedText;
  terms: LocalizedText;
  imageUrl: string | null;
  startAt: string;
  endAt: string;
  weekdays: number[] | null;
  timeFrom: string | null;
  timeTo: string | null;
  tierIds: number[] | null;
  minAmount: number | null;
  shopNames: LocalizedText[] | null;
  rules: Array<{ condition: CampaignCondition; gifts: CampaignGiftPublic[] }>;
}

/** What one campaign gave (or would give) a sale or sign-up. */
export interface AppliedCampaign {
  id: string;
  no: number;
  name: LocalizedText;
  /** Points above normal points: its multiplier (when it was the highest) and extra points. */
  points: number;
  /** The multiplier that counted, when it is this campaign's. */
  multiplier: number | null;
  coupons: Array<{ rewardId: string; title: LocalizedText; quantity: number }>;
}

/** 活動記錄: one rule's gifts given to one member. */
export interface CampaignLogRow {
  id: string;
  createdAt: string;
  memberId: string;
  memberNo: string;
  memberName: string;
  /** The sale's invoice number; null for sign-ups and sales recorded without one. */
  invoiceNo: string | null;
  /** The sale's net amount; null for sign-ups. */
  amount: number | null;
  /** The rule's condition at the time. */
  condition: CampaignCondition;
  points: number;
  multiplier: number | null;
  coupons: Array<{ title: LocalizedText; quantity: number }>;
}

// ---------------------------------------------------------------- request body

const hkMinute = z.string().regex(/^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d$/, 'YYYY-MM-DDTHH:MM');
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM');
const amount = z.number().min(0).max(100_000_000);
const times = z.number().int().min(0).max(100_000_000);

export const campaignConditionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('transaction'), mode: z.enum(['single', 'accumulative']), min: amount.nullable(), max: amount.nullable() }),
  z.object({ type: z.literal('product'), skus: z.array(z.string().trim().min(1).max(40)).min(1).max(20), quantity: z.number().int().min(1).max(9999), repeat: z.boolean() }),
  z.object({ type: z.literal('registration') }),
]);

export const campaignGiftSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('points'), points: z.number().int().min(1).max(1_000_000) }),
  z.object({ type: z.literal('multiplier'), multiplier: z.number().gt(1).max(20) }),
  z.object({ type: z.literal('coupon'), rewardId: z.string().min(1).max(64), quantity: z.number().int().min(1).max(10) }),
]);

export const campaignRuleSchema = z
  .object({
    id: z.string().regex(/^[A-Za-z0-9_-]{1,40}$/),
    condition: campaignConditionSchema,
    gifts: z.array(campaignGiftSchema).min(1).max(5),
    limit: times,
  })
  .refine((r) => r.condition.type !== 'transaction' || r.condition.mode !== 'single' || r.condition.min === null || r.condition.max === null || r.condition.max >= r.condition.min, {
    message: 'max below min',
    path: ['condition', 'max'],
  })
  .refine((r) => r.condition.type !== 'transaction' || r.condition.mode !== 'accumulative' || (r.condition.min ?? 0) > 0, { message: 'set the spend to reach', path: ['condition', 'min'] })
  .refine((r) => r.gifts.filter((g) => g.type === 'multiplier').length <= 1, { message: 'one multiplier per rule', path: ['gifts'] })
  .refine((r) => r.condition.type !== 'registration' || r.gifts.every((g) => g.type !== 'multiplier'), { message: 'a sign-up has no points to multiply', path: ['gifts'] });

export const campaignSchema = z
  .object({
    name: localizedTextSchema,
    description: localizedTextSchema,
    terms: localizedTextSchema,
    imageKey: z.string().max(300).nullable(),
    startAt: hkMinute,
    endAt: hkMinute,
    weekdays: z.array(z.number().int().min(0).max(6)).min(1).max(7).nullable(),
    timeFrom: hhmm.nullable(),
    timeTo: hhmm.nullable(),
    tierIds: z.array(z.number().int()).min(1).max(50).nullable(),
    shopIds: z.array(z.string().min(1).max(64)).min(1).max(50).nullable(),
    minAmount: amount.nullable(),
    totalLimit: times,
    memberLimit: times,
    rules: z.array(campaignRuleSchema).min(1).max(20),
    active: z.boolean(),
    aiTone: z.enum(AI_COPY_TONES).nullable().optional(),
  })
  .refine((v) => v.endAt > v.startAt, { message: 'end before start', path: ['endAt'] })
  .refine((v) => (v.timeFrom === null) === (v.timeTo === null), { message: 'set both times or neither', path: ['timeTo'] })
  .refine((v) => new Set(v.rules.map((r) => r.id)).size === v.rules.length, { message: 'rule ids repeat', path: ['rules'] });
export type CampaignInput = z.infer<typeof campaignSchema>;

// ---------------------------------------------------------------- wording (brand portal and CSV export)

export const CAMPAIGN_CONDITION_LABELS: Record<CampaignConditionType, string> = { transaction: '交易', product: '購買商品', registration: '新會員登記' };

/** "P0007" */
export const campaignCode = (no: number) => `P${String(no).padStart(4, '0')}`;

const money = (n: number) => `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

/** 「單次消費 $500 至 $1,000」, 「累積消費滿 $2,000」, 「購買 1221232 滿 2 件（每 2 件送一次）」, 「新會員登記」. */
export function campaignConditionText(c: CampaignCondition): string {
  if (c.type === 'registration') return '新會員登記';
  if (c.type === 'product') {
    const qty = c.quantity > 1 ? ` 滿 ${c.quantity} 件` : '';
    return `購買 ${c.skus.join('、')}${qty}${c.repeat ? `（每 ${c.quantity} 件送一次）` : ''}`;
  }
  if (c.mode === 'accumulative') return `累積消費滿 ${money(c.min ?? 0)}`;
  if (c.min !== null && c.max !== null) return `單次消費 ${money(c.min)} 至 ${money(c.max)}`;
  if (c.min !== null) return `單次消費滿 ${money(c.min)}`;
  if (c.max !== null) return `單次消費 ${money(c.max)} 或以下`;
  return '任何消費';
}

/** 「加送 500 積分」, 「積分 ×2」, 「$50 現金券 ×1」. */
export function campaignGiftText(g: CampaignGift, couponTitle: string): string {
  if (g.type === 'points') return `加送 ${g.points.toLocaleString('en-US')} 積分`;
  if (g.type === 'multiplier') return `積分 ×${g.multiplier}`;
  return `${couponTitle || '優惠券'} ×${g.quantity}`;
}

/** What a history row gave, e.g. 「積分 ×2（+200 分）、$50 現金券 ×1」. */
export function campaignLogGiftText(r: Pick<CampaignLogRow, 'points' | 'multiplier' | 'coupons'>, lang: Parameters<typeof pickText>[1]): string {
  const parts: string[] = [];
  if (r.multiplier) parts.push(`積分 ×${r.multiplier}（+${r.points.toLocaleString('en-US')} 分）`);
  else if (r.points) parts.push(`加送 ${r.points.toLocaleString('en-US')} 積分`);
  for (const c of r.coupons) parts.push(`${pickText(c.title, lang) || '優惠券'} ×${c.quantity}`);
  return parts.join('、') || '—';
}

// ---------------------------------------------------------------- the engine

/** What the engine needs of a campaign. */
export type CampaignForEngine = Pick<CampaignSettings, 'active' | 'startAt' | 'endAt' | 'weekdays' | 'timeFrom' | 'timeTo' | 'tierIds' | 'shopIds' | 'minAmount' | 'totalLimit' | 'memberLimit' | 'rules'> & {
  id: string;
};

export interface CampaignEvent {
  kind: 'sale' | 'registration';
  /** Net amount of the sale (0 for a sign-up). */
  amount: number;
  /** Points the sale earns without any campaign (0 for a sign-up). */
  basePoints: number;
  lines: SaleLine[];
  /** The member's level before the sale (the entry level for a sign-up). */
  tierId: number | null;
  shopId: string | null;
}

/** Use of one campaign so far, overall and by this member. */
export interface CampaignCounts {
  uses: number;
  memberUses: number;
  /** Times each rule's gifts were given (by rule id), overall and to this member. */
  ruleTimes: Record<string, number>;
  memberRuleTimes: Record<string, number>;
  /** This member's spend on sales that counted for the campaign, before this one. */
  spend: number;
}
export const NO_CAMPAIGN_COUNTS: CampaignCounts = { uses: 0, memberUses: 0, ruleTimes: {}, memberRuleTimes: {}, spend: 0 };

/** One rule whose gifts are given. */
export interface CampaignAward {
  campaignId: string;
  rule: CampaignRule;
  times: number;
  /** Extra points × times, plus the multiplier's extra points when this rule holds the highest one. */
  points: number;
  multiplier: number | null;
  coupons: Array<{ rewardId: string; quantity: number }>;
}

export interface CampaignResult {
  /** Points above normal points, from every campaign. */
  points: number;
  awards: CampaignAward[];
  /** Campaigns the sale counts for (their accumulated spend grows by the sale's amount). */
  counted: string[];
}

/** Hong Kong wall clock (UTC+8, no daylight saving). */
export function hkClock(now: Date): { minute: string; time: string; weekday: number } {
  const hk = new Date(now.getTime() + 8 * 3600_000);
  const s = hk.toISOString();
  return { minute: s.slice(0, 16), time: s.slice(11, 16), weekday: hk.getUTCDay() };
}

/** Running at `now`: active, within the period, on one of its weekdays and within its hours. */
export function campaignRuns(c: Pick<CampaignForEngine, 'active' | 'startAt' | 'endAt' | 'weekdays' | 'timeFrom' | 'timeTo'>, now: Date): boolean {
  const t = hkClock(now);
  if (!c.active || t.minute < c.startAt || t.minute > c.endAt) return false;
  if (c.weekdays && !c.weekdays.includes(t.weekday)) return false;
  return !(c.timeFrom && c.timeTo && (t.time < c.timeFrom || t.time > c.timeTo));
}

/** The sale or sign-up counts for the campaign (see the rules at the top). */
export function campaignCounts(c: CampaignForEngine, e: CampaignEvent, now: Date): boolean {
  if (!campaignRuns(c, now)) return false;
  if (c.tierIds && (e.tierId === null || !c.tierIds.includes(e.tierId))) return false;
  if (e.kind === 'registration') return true;
  if (c.shopIds && (!e.shopId || !c.shopIds.includes(e.shopId))) return false;
  return c.minAmount === null || e.amount >= c.minAmount;
}

/** A SKU as compared: letter case and spaces ignored (also 儲值 SKU and 付費會籍 SKUs). */
export const skuKey = (s: string) => s.replace(/\s+/g, '').toUpperCase();

/** Times the rule's condition is met by the event (0 = not met), before its limit. */
export function conditionTimes(c: CampaignCondition, e: CampaignEvent, counts: Pick<CampaignCounts, 'spend'> & { givenToMember: number }): number {
  if (c.type === 'registration') return e.kind === 'registration' ? 1 : 0;
  if (e.kind !== 'sale') return 0;
  if (c.type === 'product') {
    const skus = new Set(c.skus.map(skuKey));
    const qty = e.lines.reduce((n, l) => (skus.has(skuKey(l.sku)) ? n + l.quantity : n), 0);
    if (qty < c.quantity) return 0;
    return c.repeat ? Math.floor(qty / c.quantity) : 1;
  }
  if (c.mode === 'accumulative') return counts.givenToMember === 0 && counts.spend + e.amount >= (c.min ?? 0) ? 1 : 0;
  return e.amount >= (c.min ?? 0) && (c.max === null || e.amount <= c.max) ? 1 : 0;
}

/**
 * What a sale or sign-up gets from the campaigns (oldest first), given their use so far. Nothing is
 * saved; the caller records the awards (and the accumulated spend of `counted`).
 */
export function evaluateCampaigns(list: Array<{ campaign: CampaignForEngine; counts: CampaignCounts }>, e: CampaignEvent, now = new Date()): CampaignResult {
  const counted: string[] = [];
  const candidates: Array<Omit<CampaignAward, 'points' | 'multiplier'> & { extra: number; multiplier: number | null }> = [];
  for (const { campaign: c, counts } of list) {
    if (!campaignCounts(c, e, now)) continue;
    if (e.kind === 'sale') counted.push(c.id);
    if ((c.totalLimit > 0 && counts.uses >= c.totalLimit) || (c.memberLimit > 0 && counts.memberUses >= c.memberLimit)) continue;
    for (const rule of c.rules) {
      let n = conditionTimes(rule.condition, e, { spend: counts.spend, givenToMember: counts.memberRuleTimes[rule.id] ?? 0 });
      if (rule.limit > 0) n = Math.min(n, rule.limit - (counts.ruleTimes[rule.id] ?? 0));
      if (n <= 0) continue;
      const multiplier = rule.gifts.find((g) => g.type === 'multiplier');
      candidates.push({
        campaignId: c.id,
        rule,
        times: n,
        extra: rule.gifts.reduce((p, g) => (g.type === 'points' ? p + g.points * n : p), 0),
        multiplier: multiplier && e.kind === 'sale' ? multiplier.multiplier : null,
        coupons: rule.gifts.flatMap((g) => (g.type === 'coupon' ? [{ rewardId: g.rewardId, quantity: g.quantity * n }] : [])),
      });
    }
  }
  // Only the highest multiplier counts; it goes to the first rule that has it.
  const best = Math.max(1, ...candidates.map((x) => x.multiplier ?? 1));
  const holder = best > 1 ? candidates.find((x) => x.multiplier === best) : undefined;
  const multiplied = Math.floor(e.basePoints * best) - e.basePoints;
  const awards: CampaignAward[] = [];
  for (const x of candidates) {
    const mine = x === holder;
    const points = x.extra + (mine ? multiplied : 0);
    // A rule left with nothing (its only gift was a lower multiplier) is not given.
    if (points <= 0 && x.coupons.length === 0) continue;
    awards.push({ campaignId: x.campaignId, rule: x.rule, times: x.times, points, multiplier: mine ? best : null, coupons: x.coupons });
  }
  return { points: awards.reduce((p, a) => p + a.points, 0), awards, counted };
}
